import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Form } from "antd";
import FormItemWrapper from "components/basic/FormWrapper/FormItemWrapper";
import AutoCompleteAddressInput from "components/basic/SignUpForm/AutoCompleteAddressInput";
import FloatInput from "components/inputs/Form/Inputs/FloatInput";
import { QuestionType } from "enums/questionnaire";
import { isPostcodeValid } from "utils/validation";

import { countryToFlagEmoji, isCountrySupported } from "../../../../../utils/address";
import { Question } from "../../constants/questions";

type ErrorsT = {
    [key: string]: string;
};

interface AddressQuestionProps {
    data: Question;
    onAnswer: (_question: string, _answer: string | undefined, _id: string) => void;
    initialValue: any;
    setContinueDisabled: (_value: boolean) => void;
    nonUKCountryEnabled?: boolean;
}

/* eslint-disable no-unused-vars */
export enum AddressQuestionInputIds {
    AUTOCOMPLETE_ADDRESS = "autocompleteAddress",
    ADDRESS1 = "address1",
    ADDRESS2 = "address2",
    CITY = "city",
    POSTCODE = "postcode",
    COUNTRY = "country",
}

const baseInputs = [
    {
        id: AddressQuestionInputIds.ADDRESS1,
        name: "Address Line 1",
        required: true,
        type: QuestionType.textQuestion,
    },
    {
        id: AddressQuestionInputIds.ADDRESS2,
        name: "Address Line 2",
        required: false,
        type: QuestionType.textQuestion,
    },
    {
        id: AddressQuestionInputIds.CITY,
        name: "Town/City",
        required: true,
        type: QuestionType.textQuestion,
    },
    {
        id: AddressQuestionInputIds.POSTCODE,
        name: "Postcode",
        required: true,
        type: QuestionType.textQuestion,
    },
];

const countryInput = {
    id: AddressQuestionInputIds.COUNTRY,
    name: "Country",
    required: true,
    type: QuestionType.textQuestion,
};

const AddressQuestion: React.FC<AddressQuestionProps> = ({
    data,
    onAnswer,
    initialValue,
    setContinueDisabled,
    nonUKCountryEnabled = false,
}) => {
    const [addressSelected, setAddressSelected] = useState<boolean>(false);
    const [inputData, setInputData] = useState<any>({});
    const [errors, setErrors] = useState<ErrorsT>({});

    // Create dynamic inputs based on nonUKCountryEnabled
    const inputs = useMemo(() => {
        return nonUKCountryEnabled ? [...baseInputs, countryInput] : baseInputs;
    }, [nonUKCountryEnabled]);

    const validate = useCallback(() => {
        const newErrors: Record<string, any> = {};
        inputs?.forEach(input => {
            const inputValue = inputData[input.id];

            if (input.id === AddressQuestionInputIds.POSTCODE) {
                const postcodeValid = isPostcodeValid(inputValue);
                if (!postcodeValid && !nonUKCountryEnabled) {
                    newErrors[input.id] = "Please enter a valid postcode";
                }
            }

            if (input.id === "country") {
                const countrySupported = isCountrySupported(inputValue);
                if (!countrySupported && nonUKCountryEnabled) {
                    newErrors[input.id] = "TaxZap does not currently support income from this country.";
                }
            }

            if (input.required && !inputValue) {
                newErrors[input.id] = "This field is required";
            }
        });
        return newErrors;
    }, [inputData, inputs, nonUKCountryEnabled]);

    const handleInputChange = useCallback(
        ({ name, value, object } : {name?: string, value?: string, object?: {[key: string]: string}}) => {
            if (name) {
                const updatedObject = { ...inputData, [name]: value };
                setInputData(updatedObject);
            } else if (object) {
                const updatedObject = { ...inputData, ...object };
                setInputData(updatedObject);
            } else {
                throw new Error("Name or object are required");
            }
        },
        [inputData]
    );

    useEffect(() => {
        const errors = validate();
        setErrors(errors);
        if (Object.keys(errors).length > 0) {
            setContinueDisabled(true);
        } else {
            setContinueDisabled(false);
            onAnswer(data.question, inputData, data.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [inputData, validate]);

    useEffect(() => {
        if (typeof initialValue === "object" && initialValue !== null) {
            setInputData(initialValue);
            setAddressSelected(true);
        } else {
            setInputData({ [AddressQuestionInputIds.ADDRESS1]: initialValue });
        }
    }, [initialValue]);

    useEffect(() => {
        return () => {
            // Cleanup on unmount
            setContinueDisabled(false);
        };
    }, [setContinueDisabled]);

    return (
        <div className="new-questionnaire__question w335px mAuto">
            {data.questionTitle && (
                <div className="new-questionnaire__list-question-title">
                    {data.questionTitle}
                </div>
            )}
            <div
                className="new-questionnaire__question-text mb10"
                style={data.questionTitle ? { margin: "4px auto" } : {}}
            >
                {data.question}
            </div>
            <Form className="w100" layout="vertical">
                <FormItemWrapper error={""} required={false}>
                    <AutoCompleteAddressInput
                        value={inputData[AddressQuestionInputIds.AUTOCOMPLETE_ADDRESS] || ""}
                        onChange={(value: string, object?: { [key: string]: string }) => {
                            if (object) {
                                setAddressSelected(true);
                            }
                            handleInputChange({
                                object: {
                                    [AddressQuestionInputIds.AUTOCOMPLETE_ADDRESS]: value,
                                    ...(object || {}),
                                },
                            });
                        }}
                        placeholder={"Search address"}
                        nonUKCountryEnabled={nonUKCountryEnabled}
                    />
                </FormItemWrapper>

                {addressSelected &&
                    inputs.map((innerInput, index) => (
                        <FormItemWrapper
                            key={innerInput.id}
                            error={errors[innerInput.id]}
                            label={
                                innerInput.id === "country"
                                    ? `${countryToFlagEmoji(
                                        inputData[innerInput.id] || ""
                                    )} ${innerInput.name}`
                                    : innerInput.name
                            }
                            required={innerInput.required}
                        >
                            <FloatInput
                                required={innerInput.required}
                                index={index}
                                value={inputData[innerInput.id]}
                                error={errors[innerInput.id]}
                                onChange={(event: any) => {
                                    handleInputChange({
                                        name: innerInput.id,
                                        value: event.target.value,
                                    });
                                }}
                            />
                        </FormItemWrapper>
                    ))}
            </Form>
        </div>
    );
};

export default AddressQuestion;
